import {
  type ProjectInfo,
  type Share,
  type ShareViewEvent,
  uploadLimit,
  uploadTooLarge,
} from "~/protocol";
import type { BillingInfo, Credits, Pack, TopUpSetting } from "~/protocol/billing";
import type { ApiKeyInfo, ApiKeyMinted } from "~/protocol/keys";
import { signIn } from "~/web/src/auth";
import { billingOn } from "~/web/src/billing-on";
import { errorText, ShownError } from "~/web/src/errors";
import { bytes } from "~/web/src/format";

// The node-level HTTP surface (spec: docs/project/web-workspace-spec.md).
// Everything project-scoped lives behind /api/projects/:id — the v2 proxy
// seam — so the URL builders here are the only place that prefix exists.

export const projectBase = (id: string): string => `/api/projects/${encodeURIComponent(id)}`;

export function fileUrl(id: string, name: string, version?: number): string {
  const v = version === undefined ? "" : `?v=${version}`;
  return `${projectBase(id)}/files/${encodeURIComponent(name)}${v}`;
}

export function wsUrl(id: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${projectBase(id)}/ws`;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // The session lapsed under an open tab: back through sign-in to this URL.
    if (res.status === 401) void signIn(location.href);
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/** The org's projects; `mine` narrows to the ones the session's user owns. */
export const listProjects = (filter?: "mine"): Promise<ProjectInfo[]> =>
  fetch(filter ? "/api/projects?filter=mine" : "/api/projects").then((r) => json<ProjectInfo[]>(r));

/** The row as the server sees it; a 404 (malformed id, unknown, deleted, another org's) throws NotFound. */
export class NotFound extends Error {}

export async function getProject(id: string): Promise<ProjectInfo> {
  const res = await fetch(projectBase(id), { cache: "no-store" });
  if (res.status === 404) throw new NotFound(`no project '${id}'`);
  return json<ProjectInfo>(res);
}

export const createProject = (name: string, targetUrl: string): Promise<ProjectInfo> =>
  fetch("/api/projects", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name, targetUrl }),
  }).then((r) => json<ProjectInfo>(r));

/** The org's plan, seats and credits. `session` is the Checkout Session id Stripe sends the
 * browser back with; the server reads that session from Stripe and trusts nothing else of it. */
export async function getBilling(session?: string, fromPortal = false): Promise<BillingInfo> {
  const url = new URL("/api/billing", window.location.href);
  if (session) url.searchParams.set("session", session);
  // Back from Stripe's portal: the server re-reads the subscription before answering.
  if (fromPortal) url.searchParams.set("from", "portal");
  return json<BillingInfo>(await fetch(url, { cache: "no-store" }));
}

/** The org's balance, for any member: a ledger read, no Stripe call. */
export const getCredits = (): Promise<Credits> =>
  fetch("/api/billing/credits", { cache: "no-store" }).then((r) => json<Credits>(r));

/** Stripe Checkout's URL for subscribing the org; an org admin's only. */
export const startCheckout = (): Promise<{ url: string }> =>
  fetch("/api/billing/checkout", { method: "POST" }).then((r) => json<{ url: string }>(r));

/** Stripe Checkout's URL for one credit pack, named by its dollars; an org admin's only. */
export const buyPack = (pack: Pack): Promise<{ url: string }> =>
  fetch("/api/billing/packs", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ pack }),
  }).then((r) => json<{ url: string }>(r));

/** Turns automatic top-up on with this setting, or off with null; an org admin's only. */
export const setTopUp = (setting: TopUpSetting | null): Promise<{ ok: true }> =>
  fetch("/api/billing/topup", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(setting ?? { pack: null }),
  }).then((r) => json<{ ok: true }>(r));

/** The org's API keys, newest first, revoked ones included. */
export const listApiKeys = (): Promise<ApiKeyInfo[]> =>
  fetch("/api/keys", { cache: "no-store" }).then((r) => json<ApiKeyInfo[]>(r));

/** Mints a key; the token in the answer is shown this once. */
export const createApiKey = (name: string): Promise<ApiKeyMinted> =>
  fetch("/api/keys", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  }).then((r) => json<ApiKeyMinted>(r));

/** Ends a key; the row stays, listed as revoked. */
export async function revokeApiKey(id: string): Promise<void> {
  const res = await fetch(`/api/keys/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) await json<never>(res);
}

/** Stripe's portal for the org's subscription (card, invoices, cancel); an org admin's only. */
export const openPortal = (): Promise<{ url: string }> =>
  fetch("/api/billing/portal", { method: "POST" }).then((r) => json<{ url: string }>(r));

/** 204 on success; the row becomes a tombstone and the files are gone. 409 while the agent runs. */
export async function deleteProject(id: string): Promise<void> {
  const res = await fetch(projectBase(id), { method: "DELETE" });
  if (!res.ok) await json<never>(res); // throws with the server's message
}

export async function readTextFile(
  id: string,
  name: string,
): Promise<{ text: string; version: number }> {
  const res = await fetch(fileUrl(id, name), { cache: "no-store" });
  if (!res.ok) throw new Error(`${res.status} reading ${name}`);
  return { text: await res.text(), version: Number(res.headers.get("x-file-version")) };
}

/** The server's 413: over the org's limit; `upgrade` when Pro would have taken the file. */
export class TooLarge extends Error {
  constructor(
    message: string,
    public readonly upgrade: boolean,
  ) {
    super(message);
  }
}

/** PUT never overwrites: the server returns the name it actually wrote (stem-N.ext on a clash). */
export async function uploadFile(
  id: string,
  file: File,
): Promise<{ name: string; version: number }> {
  const res = await fetch(fileUrl(id, file.name), { method: "PUT", body: file });
  if (res.status === 413) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; upgrade?: boolean };
    throw new TooLarge(body.error ?? "This file is over the upload limit.", body.upgrade === true);
  }
  return json<{ name: string; version: number }>(res);
}

/**
 * Uploads in order; every file gets its try. Throws one ShownError naming the
 * failures at the end, so the successes have already landed by then. A file
 * over the org's limit (`uploadLimit(subscribed)`, `subscribed` from the
 * snapshot's org) is refused here with the server's own words before a byte
 * goes out; the server's 413 stays the failsafe. When Pro would have taken
 * the file the error offers the billing page.
 */
export async function uploadFiles(
  id: string,
  files: Iterable<File>,
  subscribed: boolean,
): Promise<void> {
  const limit = uploadLimit(subscribed);
  const failed: string[] = [];
  let upgrade = false;
  for (const f of files) {
    const label = `${f.name} (${bytes(f.size)})`;
    if (f.size > limit) {
      const refusal = uploadTooLarge(limit);
      if (refusal.upgrade) upgrade = true;
      failed.push(`${label}: ${refusal.error}`);
      continue;
    }
    try {
      await uploadFile(id, f);
    } catch (e) {
      if (e instanceof TooLarge && e.upgrade) upgrade = true;
      failed.push(`${label}: ${errorText(e)}`);
    }
  }
  if (failed.length) {
    throw new ShownError(
      failed.join("\n"),
      failed.length === 1 ? "File not uploaded" : "Some files were not uploaded",
      upgrade && billingOn ? { label: "Go Pro", href: "/billing" } : undefined,
    );
  }
}

export function shareWSURL(id: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/svws/${id}`;
}

export async function getShare(id: string): Promise<Share> {
  const res = await fetch(`/api/shares/${encodeURIComponent(id)}`, { cache: "no-store" });
  if (res.status === 404) throw new NotFound(`no share '${id}'`);
  return json<Share>(res);
}

export type listSharesOptions = {
  projectId?: string;
  orderBy?: string;
  orderByDirection?: "asc" | "desc";
};

export async function listShares(opts?: listSharesOptions): Promise<Share[]> {
  const url = new URL("/api/shares", window.location.href);
  if (opts?.projectId) {
    url.searchParams.set("projectId", opts.projectId);
  }
  if (opts?.orderBy) {
    url.searchParams.set("orderBy", opts?.orderBy);
  }
  if (opts?.orderByDirection) {
    url.searchParams.set("orderByDirection", opts?.orderByDirection);
  }
  return fetch(url).then((r) => json<Share[]>(r));
}

export type listShareEventsOptions = {
  projectId?: string;
  shareId?: string;
  orderBy?: string;
  orderByDirection?: "asc" | "desc";
};

// TODO: standardized pagination, filter, ordering arguments
export async function listShareEvents(opts?: listShareEventsOptions): Promise<ShareViewEvent[]> {
  const url = new URL("/api/share_events", window.location.href);
  if (opts?.projectId) {
    url.searchParams.set("projectId", opts.projectId);
  }
  if (opts?.shareId) {
    url.searchParams.set("shareId", opts?.shareId);
  }
  if (opts?.orderBy) {
    url.searchParams.set("orderBy", opts?.orderBy);
  }
  if (opts?.orderByDirection) {
    url.searchParams.set("orderByDirection", opts?.orderByDirection);
  }
  return fetch(url).then((r) => json<ShareViewEvent[]>(r));
}
